/** The case a person is in, and why they need staff: the line under the name, and the lists that name licence changes and held people. */
import type { HeldPerson } from "../../api/audienceReview.types";
import { getPersonCase } from "../../api/person360";
import type { PersonFull } from "../../api/person360.types";
import type { StateLicenseChange } from "../../api/stateLicense.types";
import { shortDate } from "../data/format";
import { useQuery } from "../data/useQuery";
import type { RowFacts } from "../screens/people/selection";
import { VIEW } from "../data/queues";
import { caseKindOf, QUEUE_KEY, type CaseKind } from "./review";

export interface Case {
  kind: CaseKind | null;
  /** What a review is keyed on; null when the case cannot be reviewed (none, or held). */
  queueKey: string | null;
  /** The first line under the name: why this person needs staff. "" when they are in no queue. */
  reason: string;
  licence?: StateLicenseChange;
  held?: HeldPerson;
  /** The lists that may still name a case are loading. */
  settling: boolean;
}

const days = (n: number) => `${n} ${n === 1 ? "day" : "days"}`;
const money = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

function reasonOf(kind: CaseKind | null, person: PersonFull, facts: RowFacts | undefined, held: HeldPerson | undefined, viewId: string): string {
  const { standing, payments, membership_journey: journey, lifecycle } = person;
  switch (kind) {
    case "payment": {
      const row = journey.current_membership_rows[0];
      const what = /fail/i.test(row?.payment_status ?? "") ? "Payment failed" : "Payment due";
      return [what, [shortDate(payments.failing_since), row ? money(row.fee_cents) : ""].filter((part) => part && part !== "—").join(", ")].filter(Boolean).join(" ");
    }
    case "grace": {
      const left = facts?.left !== undefined ? Number(facts.left) : standing.grace;
      if (left == null || !Number.isFinite(left)) return "Lapsed";
      // The lists' words for the last day: the grace ends, or (66 to 120 days) the member is suspended.
      return left === 0 ? `${viewId === VIEW.lapsed120 ? "Suspends" : "Grace ends"} today` : `${days(left)} left`;
    }
    case "suspended": {
      const since = lifecycle.events.filter((e) => e.toStanding === "suspended").map((e) => e.effectiveAt).sort().pop();
      return since ? `Suspended ${shortDate(since)}` : "Suspended";
    }
    case "licence":
      return "Licence changed";
    case "held":
      return held?.reasonLabel ?? "Held for review";
    default:
      return "";
  }
}

export const caseKey = (id: number): string => `v2:person:${id}:case`;

/**
 * `id` is the person the sheet is on, `person` their record once it has landed. `viewId` is the People view the sheet is open over ("" elsewhere): it
 * says which queue the person was picked from.
 */
export function useCase(id: number, person: PersonFull | undefined, viewId: string, facts: RowFacts | undefined): Case {
  const base = person ? caseKindOf(viewId, person.standing.standing, !!person.payments.failing_since) : null;
  // Licence changes and held people are lists of their own. The person's own answer (#714), asked beside the person, never after them.
  const own = useQuery(caseKey(id), () => getPersonCase(id));

  // Only where it is the case: a held person picked from a payment queue is worked as a payment problem.
  const licence = base === null || base === "licence" ? (own.data?.licence ?? undefined) : undefined;
  const held = base === null || base === "held" ? (own.data?.held ?? undefined) : undefined;
  const kind: CaseKind | null = base ?? (licence ? "licence" : held ? "held" : null);
  return {
    kind,
    queueKey: kind && kind !== "held" ? QUEUE_KEY[kind] : null,
    reason: person ? reasonOf(kind, person, facts, held, viewId) : "",
    licence,
    held,
    settling: !!person && base === null && own.loading,
  };
}
